import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../styles/CreateEvent.css";
import { API_BASE_URL } from "../config";
import FormBuilderModal from "../components/FormBuilderModal";

const EVENT_TYPES_ENDPOINT = `${API_BASE_URL}/event_type`;

// Convert base64 → preview URL (safe)
const getBase64Preview = (base64) => {
  try {
    if (!base64) return null;

    // already full format
    if (base64.startsWith("data:image")) {
      return base64;
    }

    // fallback (just in case)
    return `data:image/jpeg;base64,${base64}`;
  } catch (err) {
    console.error("Error parsing base64 image:", err);
    return null;
  }
};

const formatDateOnly = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  return date.toISOString().split("T")[0];
};


// Convert base64 → File
const base64ToFile = async (base64, fileName = "image.jpg") => {
  try {
    const res = await fetch(base64);
    const blob = await res.blob();
    return new File([blob], fileName, { type: blob.type });
  } catch (err) {
    console.error("Error converting base64 to file:", err);
    return null;
  }
};

// Fallback event types in case API fails
const FALLBACK_EVENT_TYPES = [
  { id: 1, type: "Conference", icon: "fas fa-users" },
  { id: 2, type: "Meetup", icon: "fas fa-handshake" },
  { id: 3, type: "Exhibition", icon: "fas fa-landmark" },
  { id: 4, type: "Concert", icon: "fas fa-music" },
  { id: 5, type: "Marathon", icon: "fas fa-running" },
  { id: 6, type: "Other", icon: "fas fa-calendar-alt" },
];

// Icon mapping for event types
const EVENT_TYPE_ICONS = {
  conference: "fas fa-users",
  conferance: "fas fa-users",
  meetup: "fas fa-handshake",
  exhibition: "fas fa-landmark",
  expo: "fas fa-landmark",
  concert: "fas fa-music",
  marathon: "fas fa-running",
  event: "fas fa-calendar-alt",
  other: "fas fa-calendar-alt",
  default: "fas fa-calendar",
};

// Description mapping for event types
const EVENT_TYPE_DESCRIPTIONS = {
  conference: "Professional gathering for knowledge sharing",
  conferance: "Professional gathering for knowledge sharing",
  meetup: "Casual gathering for networking",
  exhibition: "Showcase of products and innovations",
  expo: "Showcase of products and innovations",
  concert: "Live music performance event",
  marathon: "Long-distance running competition",
  event: "Custom event type",
  other: "Custom event type",
};

const CITIES = [
  // Top 10 Metros
  "Mumbai",
  "Delhi",
  "Bangalore",
  "Hyderabad",
  "Chennai",
  "Kolkata",
  "Pune",
  "Ahmedabad",
  "Jaipur",
  "Surat",
  "Other",

  // Next 20 Major Cities
  "Lucknow",
  "Nagpur",
  "Indore",
  "Bhopal",
  "Visakhapatnam",
  "Patna",
  "Vadodara",
  "Ghaziabad",
  "Ludhiana",
  "Agra",
  "Nashik",
  "Faridabad",
  "Meerut",
  "Rajkot",
  "Varanasi",
  "Srinagar",
  "Aurangabad",
  "Amritsar",
  "Coimbatore",
  "Jabalpur",

  // Additional Major Cities
  "Gwalior",
  "Vijayawada",
  "Jodhpur",
  "Madurai",
  "Raipur",
  "Kota",
  "Guwahati",
  "Chandigarh",
  "Solapur",
  "Thane",
  "Allahabad",
  "Ranchi",
  "Gurgaon",
  "Bhubaneswar",
  "Kochi",
  "Jamshedpur",
  "Dehradun",
  "Mysore",
  "Jalandhar",
  "Bareilly",
];

// Transform API response to form data format
const transformEventData = (eventData) => {
  console.log("Transforming event data:", eventData);

  // Helper to format date for input fields (YYYY-MM-DDTHH:mm)
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";

    try {
      // Keep the date/time exactly as received from the API.
      // Do NOT convert UTC -> local timezone.
      const value = dateString.replace("Z", "");

      const [datePart, timePart] = value.split("T");

      if (!datePart || !timePart) return "";

      const [year, month, day] = datePart.split("-");
      const [hours, minutes] = timePart.split(":");

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  // Parse metadata from API response
  const metadata = eventData.metadata || {};

  const attendeesConfig =
    metadata.attendees_configuration ||
    metadata.attendeeConfig ||
    eventData.attendeeConfig ||
    {};

  const speakerConfig =
    metadata.speaker_configuration ||
    metadata.speakerConfig ||
    eventData.speakerConfig ||
    {};

  // EMPTY — will be filled from APIs
  const sponsorNames = [];
  const sponsorLogos = [];
  const sponsorCategories = [];
  const sponsorLogoUrls = [];

  const exhibitorNames = [];
  const exhibitorLogos = [];
  const exhibitorLogoUrls = [];

  // Convert base64 to Blob for file handling
  const bannerFile = eventData.banner_file;
  const logoFile = eventData.logo_file;

  let bannerBlob = null;
  let logoBlob = null;
  let bannerPreviewUrl = null;
  let logoPreviewUrl = null;

  // Convert base64 to Blob for banner
  if (bannerFile?.base64) {
    try {
      const base64Data = bannerFile.base64.split(",")[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      bannerBlob = new Blob([byteArray], { type: bannerFile.mime_type });
      bannerPreviewUrl = URL.createObjectURL(bannerBlob);
    } catch (error) {
      console.error("Error converting banner base64 to Blob:", error);
    }
  }

  // Convert base64 to Blob for logo
  if (logoFile?.base64) {
    try {
      const base64Data = logoFile.base64.split(",")[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      logoBlob = new Blob([byteArray], { type: logoFile.mime_type });
      logoPreviewUrl = URL.createObjectURL(logoBlob);
    } catch (error) {
      console.error("Error converting logo base64 to Blob:", error);
    }
  }

  // Determine if features are enabled
  const isAttendeeEnabled =
    attendeesConfig && Object.keys(attendeesConfig).length > 0;
  const isSpeakerEnabled =
    speakerConfig && Object.keys(speakerConfig).length > 0;
  const isSponsorEnabled =
    metadata.isSponser === true || metadata.isSponser === "true";
  const isExhibitorEnabled =
    metadata.isExibitor === true || metadata.isExibitor === "true";
  const org = eventData.metadata?.organizationDetails || {};
  return {
    formData: {
      event_type_id: metadata.event_type_id || "",
      name: metadata.name || "",
      description: metadata.description || "",
      start_date: formatDateForInput(metadata.start_date),
      end_date: formatDateForInput(metadata.end_date),
      venue: metadata.venue || "",
      city: metadata.city || "",
      is_paid: metadata.is_paid || false,
      ticket_price: metadata.ticket_price || "",
      is_publish: metadata.is_publish || 0,

      // ADD THESE NEW FIELDS ↓↓↓
      organization_name:
        eventData.organization_name ||
        metadata.organization_name ||
        eventData.metadata?.organizationDetails?.organization_name ||
        "",


      contact_email:
        eventData.orgorganization_emailid ||
        eventData.metadata?.orgorganization_emailid ||
        org.email_id ||
        "",

      contact_phone:
        eventData.orgorganization_contact ||
        eventData.metadata?.orgorganization_contact ||
        org.contact_number ||
        "",

      github_url:
        eventData.orgorganization_github ||
        eventData.metadata?.orgorganization_github ||
        org.github_link ||
        "",

      linkedin_url:
        eventData.orgorganization_linkedin ||
        eventData.metadata?.orgorganization_linkedin ||
        org.linkedin_link ||
        "",

      instagram_url:
        eventData.orgorganization_instagram ||
        eventData.metadata?.orgorganization_instagram ||
        org.instagram_link ||
        "",

      twitter_url:
        eventData.orgorganization_twitter ||
        eventData.metadata?.orgorganization_twitter ||
        org.twitter_link ||
        "",

      facebook_url:
        eventData.orgorganization_facebook ||
        eventData.metadata?.orgorganization_facebook ||
        org.facebook_link ||
        "",

      website_url:
        eventData.orgorganization_website ||
        eventData.metadata?.orgorganization_website ||
        org.website_link ||
        "",
    },
    selectedEventTypeId: metadata.event_type_id || "",
    selectedTemplate: null,

    // Feature flags
    enableAttendee: isAttendeeEnabled,
    enableSpeaker: isSpeakerEnabled,
    enableSponsor: isSponsorEnabled,
    enableExhibitor: isExhibitorEnabled,

    // Attendee fields
   attendeeFields: {
  registration_start_date: formatDateOnly(attendeesConfig.start_date),
  registration_end_date: formatDateOnly(attendeesConfig.end_date),
  registration_limit: attendeesConfig.default_attendee_limit || "",
  is_auto_approve: attendeesConfig.is_auto_approve !== false,

  registration_form_type:
    attendeesConfig.registration_form_type === "custom" ||
    (Array.isArray(attendeesConfig.custom_form) &&
      attendeesConfig.custom_form.length > 0)
      ? "custom"
      : "default",

  custom_form_fields: attendeesConfig.custom_form || [],
},

    // Speaker fields
    speakerFields: {
      speaker_start_date: formatDateOnly(speakerConfig.start_date),
      speaker_end_date: formatDateOnly(speakerConfig.end_date),
      speaker_limit: speakerConfig.speaker_limit || "",
      speaker_form_type: speakerConfig.speaker_form_type || "default",
      custom_speaker_form: speakerConfig.custom_form || [],
    },

    // Sponsor fields — populated from API if present
    sponsorFields: {
      sponsor_names: sponsorNames,
      sponsor_logos: sponsorLogos,
      sponsor_logo_urls: sponsorLogoUrls,
      sponsor_categories: sponsorCategories,
    },

    // Exhibitor fields — populated from API if present
    exhibitorFields: {
      exhibitor_names: exhibitorNames,
      exhibitor_logos: exhibitorLogos,
      exhibitor_logo_urls: exhibitorLogoUrls,
    },

    // Media
    banner: bannerBlob,
    logo: logoBlob,
    bannerPreview: bannerPreviewUrl,
    logoPreview: logoPreviewUrl,
    existingBannerUrl: bannerPreviewUrl,
    existingLogoUrl: logoPreviewUrl,
    bannerFileName: "existing_banner.jpg",
    logoFileName: "existing_logo.jpg",
  };
};

const EditEvent = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [eventTypes, setEventTypes] = useState(FALLBACK_EVENT_TYPES);
  const [usingFallbackData, setUsingFallbackData] = useState(false);
  const [showFormBuilderModal, setShowFormBuilderModal] = useState(false);
  const [showDefaultFormPreview, setShowDefaultFormPreview] = useState(false);
  const [customFormType, setCustomFormType] = useState("attendee");
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [activeTab, setActiveTab] = useState("attendee");
  const [sponsorCategories, setSponsorCategories] = useState([]);
  const [loadingSponsorCategories, setLoadingSponsorCategories] =
    useState(false);

  // Form state
  const [formData, setFormData] = useState({
    event_type_id: "",
    name: "",
    description: "",
    start_date: "",
    end_date: "",
    venue: "",
    city: "",
    is_paid: false,
    ticket_price: "",
    is_publish: 0,

    organization_name: "",

    // ADD THESE NEW FIELDS ↓↓↓
    contact_email: "",
    contact_phone: "",
    github_url: "",
    linkedin_url: "",
    instagram_url: "",
    twitter_url: "",
    facebook_url: "",
    website_url: "",
  });

  const [selectedEventTypeId, setSelectedEventTypeId] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [banner, setBanner] = useState(null);
  const [logo, setLogo] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [existingBannerUrl, setExistingBannerUrl] = useState(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState(null);
  const [bannerFileName, setBannerFileName] = useState("");
  const [logoFileName, setLogoFileName] = useState("");


  // Feature flags
  const [enableAttendee, setEnableAttendee] = useState(true);
  const [enableSpeaker, setEnableSpeaker] = useState(false);
  const [enableSponsor, setEnableSponsor] = useState(false);
  const [enableExhibitor, setEnableExhibitor] = useState(false);

  // Configuration fields
  const [attendeeFields, setAttendeeFields] = useState({
    registration_start_date: "",
    registration_end_date: "",
    registration_limit: "",
    is_auto_approve: true,
    registration_form_type: "default",
    custom_form_fields: [],
  });

  const [speakerFields, setSpeakerFields] = useState({
    speaker_start_date: "",
    speaker_end_date: "",
    speaker_limit: "",
    speaker_form_type: "default",
    custom_speaker_form: [],
  });

  const [sponsorFields, setSponsorFields] = useState({
    sponsor_names: [],
    sponsor_logos: [],
    sponsor_logo_urls: [],
    sponsor_categories: [],
  });

  const [exhibitorFields, setExhibitorFields] = useState({
    exhibitor_names: [],
    exhibitor_logos: [],
    exhibitor_logo_urls: [],
  });

  // Sponsor/Exhibitor add-form state
  const [newSponsorName, setNewSponsorName] = useState("");
  const [newSponsorLogo, setNewSponsorLogo] = useState(null);
  const [newSponsorCategory, setNewSponsorCategory] = useState("");
  const [newExhibitorName, setNewExhibitorName] = useState("");
  const [newExhibitorLogo, setNewExhibitorLogo] = useState(null);

  // Fetch event types
  useEffect(() => {
    const fetchEventTypes = async () => {
      try {
        console.log("Fetching event types from:", EVENT_TYPES_ENDPOINT);
        const response = await fetch(EVENT_TYPES_ENDPOINT);
        if (response.ok) {
          const apiEventTypes = await response.json();
          console.log("Event types API response:", apiEventTypes);

          if (Array.isArray(apiEventTypes) && apiEventTypes.length > 0) {
            const transformedTypes = apiEventTypes.map((type) => ({
              ...type,
              icon: getEventTypeIcon(type.type?.toLowerCase()),
            }));
            setEventTypes(transformedTypes);
            setUsingFallbackData(false);
          } else {
            console.warn("No event types returned from API, using fallback");
            setEventTypes(FALLBACK_EVENT_TYPES);
            setUsingFallbackData(true);
          }
        } else {
          console.warn(
            `API error (${response.status}), using fallback event types`,
          );
          setEventTypes(FALLBACK_EVENT_TYPES);
          setUsingFallbackData(true);
        }
      } catch (err) {
        console.error("Error loading event types:", err);
        setEventTypes(FALLBACK_EVENT_TYPES);
        setUsingFallbackData(true);
      }
    };

    fetchEventTypes();
  }, []);

  // Fetch Sponsors
  const fetchSponsors = async (eventId) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/get_sponsers/${eventId}`,
      );

      if (!res.ok) throw new Error("Failed sponsors API");

      const data = await res.json();

      if (!Array.isArray(data)) return;

      setSponsorFields({
        sponsor_names: data.map((s) => s.name || ""),
        sponsor_logos: data.map(() => null), // existing = no File
        sponsor_logo_urls: data.map((s) =>
          getBase64Preview(s?.logo_file?.base64),
        ),
        sponsor_categories: data.map((s) => s.category || ""),
      });

      // enable toggle automatically
      if (data.length > 0) setEnableSponsor(true);
    } catch (err) {
      console.error("Sponsors fetch error:", err);
    }
  };

  // Fetch Exhibitors
  const fetchExhibitors = async (eventId) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/get_exibitors/${eventId}`,
      );

      if (!res.ok) throw new Error("Failed exhibitors API");

      const data = await res.json();

      if (!Array.isArray(data)) return;

      setExhibitorFields({
        exhibitor_names: data.map((e) => e.name || ""),
        exhibitor_logos: data.map(() => null),
        exhibitor_logo_urls: data.map((e) =>
          getBase64Preview(e?.logo_file?.base64),
        ),
      });

      if (data.length > 0) setEnableExhibitor(true);
    } catch (err) {
      console.error("Exhibitors fetch error:", err);
    }
  };

  // Fetch sponsor categories
  useEffect(() => {
    const fetchSponsorCategories = async () => {
      try {
        setLoadingSponsorCategories(true);
        const res = await fetch(
          `${API_BASE_URL}/sponsers_categories`,
        );
        if (!res.ok) throw new Error("Failed to fetch sponsor categories");
        const data = await res.json();
        setSponsorCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Sponsor category fetch error:", err);
        setSponsorCategories([]);
      } finally {
        setLoadingSponsorCategories(false);
      }
    };

    fetchSponsorCategories();
  }, []);

  // Fetch event data
  useEffect(() => {
    const fetchEventData = async () => {
      try {
        setLoading(true);
        setError("");

        const token = localStorage.getItem("token");

        const response = await fetch(`${API_BASE_URL}/event/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch event: ${response.status}`);
        }

        const eventData = await response.json();

        const transformedData = transformEventData(eventData);

        // existing state updates
        setFormData(transformedData.formData);
        setSelectedEventTypeId(transformedData.selectedEventTypeId);
        setEnableAttendee(transformedData.enableAttendee);
        setEnableSpeaker(transformedData.enableSpeaker);
        setAttendeeFields(transformedData.attendeeFields);
        setSpeakerFields(transformedData.speakerFields);
        setBanner(transformedData.banner);
        setLogo(transformedData.logo);
        setBannerPreview(transformedData.bannerPreview);
        setLogoPreview(transformedData.logoPreview);
        setExistingBannerUrl(transformedData.existingBannerUrl);
        setExistingLogoUrl(transformedData.existingLogoUrl);

        await Promise.all([fetchSponsors(id), fetchExhibitors(id)]);
      } catch (err) {
        console.error("Error fetching event:", err);
        setError(err.message || "Failed to load event data");
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [id]);

  // Helper functions
  const getEventTypeIcon = (typeName) => {
    const normalizedName = typeName?.toLowerCase() || "";
    return EVENT_TYPE_ICONS[normalizedName] || EVENT_TYPE_ICONS.default;
  };

  const getEventTypeDescription = (typeName) => {
    const normalizedName = typeName?.toLowerCase() || "";
    return EVENT_TYPE_DESCRIPTIONS[normalizedName] || "Custom event type";
  };

  const formatDateForApi = (value) => {
    if (!value) return "";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    } catch (error) {
      console.error("Error formatting date for API:", error);
      return "";
    }
  };

  // Form handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEventTypeCardClick = (typeObj) => {
    // Read-only - do nothing
    console.log("Event type is read-only, cannot be changed");
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(
          `Banner image is too large. Please select an image smaller than 5MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`,
        );
        e.target.value = "";
        setBanner(null);
        setBannerPreview(null);
        setBannerFileName("");
        return;
      }

      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        alert("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
        e.target.value = "";
        setBanner(null);
        setBannerPreview(null);
        setBannerFileName("");
        return;
      }

      setBanner(file);
      setBannerFileName(file.name);
      const previewUrl = URL.createObjectURL(file);
      setBannerPreview(previewUrl);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(
          `Logo image is too large. Please select an image smaller than 5MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`,
        );
        e.target.value = "";
        setLogo(null);
        setLogoPreview(null);
        setLogoFileName("");
        return;
      }

      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        alert("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
        e.target.value = "";
        setLogo(null);
        setLogoPreview(null);
        setLogoFileName("");
        return;
      }

      setLogo(file);
      setLogoFileName(file.name);
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
    }
  };

  const handleAttendeeChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAttendeeFields((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSpeakerChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSpeakerFields((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSponsorChange = (e) => {
    const { name, value } = e.target;
    setSponsorFields((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleExhibitorChange = (e) => {
    const { name, value } = e.target;
    setExhibitorFields((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSponsorLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert("Logo is too large. Please select an image smaller than 5MB.");
        return;
      }
      setNewSponsorLogo(file);
    }
  };

  const handleExhibitorLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert("Logo is too large. Please select an image smaller than 5MB.");
        return;
      }
      setNewExhibitorLogo(file);
    }
  };

  const addSponsor = () => {
    if (!newSponsorName.trim()) {
      alert("Sponsor name is required");
      return;
    }
    if (!newSponsorLogo) {
      alert("Sponsor logo is required");
      return;
    }
    if (!newSponsorCategory) {
      alert("Sponsor category is required");
      return;
    }

    setSponsorFields((prev) => ({
      ...prev,
      sponsor_names: [...prev.sponsor_names, newSponsorName.trim()],
      sponsor_logos: [...prev.sponsor_logos, newSponsorLogo],
      sponsor_logo_urls: [...prev.sponsor_logo_urls, null],
      sponsor_categories: [...prev.sponsor_categories, newSponsorCategory],
    }));

    setNewSponsorName("");
    setNewSponsorLogo(null);
    setNewSponsorCategory("");
  };

  const removeSponsor = (index) => {
    setSponsorFields((prev) => ({
      ...prev,
      sponsor_names: prev.sponsor_names.filter((_, i) => i !== index),
      sponsor_logos: prev.sponsor_logos.filter((_, i) => i !== index),
      sponsor_logo_urls: prev.sponsor_logo_urls.filter((_, i) => i !== index),
      sponsor_categories: prev.sponsor_categories.filter((_, i) => i !== index),
    }));
  };

  const addExhibitor = () => {
    if (!newExhibitorName.trim()) {
      alert("Exhibitor name is required");
      return;
    }
    if (!newExhibitorLogo) {
      alert("Exhibitor logo is required");
      return;
    }

    setExhibitorFields((prev) => ({
      ...prev,
      exhibitor_names: [...prev.exhibitor_names, newExhibitorName.trim()],
      exhibitor_logos: [...prev.exhibitor_logos, newExhibitorLogo],
      exhibitor_logo_urls: [...prev.exhibitor_logo_urls, null],
    }));

    setNewExhibitorName("");
    setNewExhibitorLogo(null);
  };

  const removeExhibitor = (index) => {
    setExhibitorFields((prev) => ({
      ...prev,
      exhibitor_names: prev.exhibitor_names.filter((_, i) => i !== index),
      exhibitor_logos: prev.exhibitor_logos.filter((_, i) => i !== index),
      exhibitor_logo_urls: prev.exhibitor_logo_urls.filter(
        (_, i) => i !== index,
      ),
    }));
  };

  const formatDateTimeForApi = (value) => {
    if (!value) return "";

    // Convert: 2026-10-02T14:30
    // Into:   2026-10-02 14:30:00
    return `${value.replace("T", " ")}:00`;
  };

  // Prepare form data for update API
  const prepareUpdateData = async () => {
    const token = localStorage.getItem("token");
    const payload = new FormData();

    // Event ID
    payload.append("eventid", id);

    // Basic event fields
    payload.append("event_type_id", formData.event_type_id);
    payload.append("name", formData.name);
    payload.append("description", formData.description || "");

    payload.append(
      "organization_name",
      formData.organization_name?.trim() || ""
    );


    // 👇 PUT DEBUG CODE HERE
    console.log("========== DATE/TIME DEBUG ==========");
    console.log("FORM START:", formData.start_date);
    console.log("FORM END:", formData.end_date);

    console.log(
      "API START:",
      formatDateTimeForApi(formData.start_date)
    );

    console.log(
      "API END:",
      formatDateTimeForApi(formData.end_date)
    );

    payload.append("start_date", formatDateTimeForApi(formData.start_date));
    payload.append("end_date", formatDateTimeForApi(formData.end_date));
    payload.append("venue", formData.venue);
    payload.append("city", formData.city);
    payload.append("is_paid", String(formData.is_paid));
    payload.append("is_publish", String(formData.is_publish));

    // ADD THESE NEW FIELDS ↓↓↓
    payload.append("orgorganization_emailid", formData.contact_email || "");
    payload.append("orgorganization_contact", formData.contact_phone || "");
    payload.append("orgorganization_github", formData.github_url || "");
    payload.append("orgorganization_linkedin", formData.linkedin_url || "");
    payload.append("orgorganization_instagram", formData.instagram_url || "");
    payload.append("orgorganization_twitter", formData.twitter_url || "");
    payload.append("orgorganization_facebook", formData.facebook_url || "");
    payload.append("orgorganization_website", formData.website_url || "");

    if (formData.is_paid && formData.ticket_price) {
      payload.append("ticket_price", String(formData.ticket_price));
    }

    // Feature flags
    payload.append("isSpeaker", String(enableSpeaker));
    payload.append("isSponser", String(enableSponsor));
    payload.append("isExibitor", String(enableExhibitor));

    // Attendee configuration
    if (enableAttendee) {
      if (attendeeFields.registration_start_date) {
        payload.append(
          "registration_start_date",
          formatDateForApi(attendeeFields.registration_start_date),
        );
      }
      if (attendeeFields.registration_end_date) {
        payload.append(
          "registration_end_date",
          formatDateForApi(attendeeFields.registration_end_date),
        );
      }
      if (attendeeFields.registration_limit) {
        payload.append(
          "registration_limit",
          String(attendeeFields.registration_limit),
        );
      }

      console.log("========== ATTENDEE FORM DEBUG ==========");
console.log(
  "registration_form_type:",
  attendeeFields.registration_form_type
);
console.log(
  "custom_form_fields:",
  attendeeFields.custom_form_fields
);
console.log(
  "custom_form_fields length:",
  attendeeFields.custom_form_fields?.length
);
console.log("==========================================");

      const attendeeConfig = {
        start_date: formatDateForApi(attendeeFields.registration_start_date),
        end_date: formatDateForApi(attendeeFields.registration_end_date),
        custom_form:
          attendeeFields.registration_form_type === "custom"
            ? attendeeFields.custom_form_fields
            : [],
        default_attendee_limit: attendeeFields.registration_limit || 1000,
        registration_form_type: attendeeFields.registration_form_type,
        is_auto_approve: attendeeFields.is_auto_approve,
      };

      payload.append("attendeeConfig", JSON.stringify(attendeeConfig));
    }

    // Speaker configuration
    if (enableSpeaker) {
      const speakerConfig = {
        start_date: formatDateForApi(speakerFields.speaker_start_date),
        end_date: formatDateForApi(speakerFields.speaker_end_date),
        custom_form:
          speakerFields.speaker_form_type === "custom"
            ? speakerFields.custom_speaker_form
            : [],
        speaker_limit: speakerFields.speaker_limit || 50,
        speaker_form_type: speakerFields.speaker_form_type,
      };

      payload.append("speakerConfig", JSON.stringify(speakerConfig));
    }

    // Sponsor configuration
    if (enableSponsor) {
      sponsorFields.sponsor_names.forEach((name) => {
        payload.append("sponsorNames", name);
      });

      for (let i = 0; i < sponsorFields.sponsor_names.length; i++) {
        const file = sponsorFields.sponsor_logos[i];

        if (file instanceof File) {
          // new upload
          payload.append("sponsorLogos", file);
        } else if (sponsorFields.sponsor_logo_urls[i]) {
          // existing image → convert to File
          const converted = await base64ToFile(
            sponsorFields.sponsor_logo_urls[i],
            `sponsor_${i}.jpg`,
          );

          if (converted) {
            payload.append("sponsorLogos", converted);
          }
        }
      }

      sponsorFields.sponsor_categories.forEach((cat) => {
        payload.append("sponsercategory", cat);
      });
    }

    // Exhibitor configuration
    if (enableExhibitor) {
      exhibitorFields.exhibitor_names.forEach((name) => {
        payload.append("exibitorNames", name);
      });

      for (let i = 0; i < exhibitorFields.exhibitor_names.length; i++) {
        const file = exhibitorFields.exhibitor_logos[i];

        if (file instanceof File) {
          payload.append("exibitorLogos", file);
        } else if (exhibitorFields.exhibitor_logo_urls[i]) {
          const converted = await base64ToFile(
            exhibitorFields.exhibitor_logo_urls[i],
            `exhibitor_${i}.jpg`,
          );

          if (converted) {
            payload.append("exibitorLogos", converted);
          }
        }
      }
    }

    // Event Images - include both new and existing files
    console.log("Preparing image files for payload:");
    console.log("Banner:", banner ? "New file" : "No new banner");
    console.log("Logo:", logo ? "New file" : "No new logo");

    // Always include banner (either new file or existing Blob)
    if (banner) {
      payload.append("bannerfile", banner, bannerFileName || "banner.jpg");
      console.log("Added banner to payload:", bannerFileName || "banner.jpg");
    } else if (existingBannerUrl) {
      // If no new banner but existing banner exists, we need to re-upload it
      // or the backend should handle keeping the existing file
      console.log("Using existing banner file");
      // Note: If your backend expects the file to be included even when not changed,
      // you'll need to fetch the file again or store it differently
    }

    // Always include logo (either new file or existing Blob)
    if (logo) {
      payload.append("bannerlog", logo, logoFileName || "logo.jpg");
      console.log("Added logo to payload:", logoFileName || "logo.jpg");
    } else if (existingLogoUrl) {
      console.log("Using existing logo file");
    }

    // Log FormData contents for debugging
    console.log("FormData payload entries:");
    for (let [key, value] of payload.entries()) {
      if (value instanceof File) {
        console.log(
          `${key}: File - ${value.name} (${value.type}, ${value.size} bytes)`,
        );
      } else if (value instanceof Blob) {
        console.log(`${key}: Blob - ${value.type} (${value.size} bytes)`);
      } else {
        console.log(`${key}: ${value}`);
      }
    }

    return { payload, token };
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      // Validate basic info
      if (
        !formData.event_type_id ||
        !formData.name ||
        !formData.start_date ||
        !formData.end_date ||
        !formData.venue ||
        !formData.city
      ) {
        throw new Error("Please fill in all required fields marked with *");
      }

      const { payload, token } = await prepareUpdateData();

      // Log payload for debugging
      console.log("Updating event with form data:");
      console.log("=== FormData Contents ===");
      for (let [key, value] of payload.entries()) {
        if (value instanceof File || value instanceof Blob) {
          console.log(
            `${key}: File/Blob - ${value.name || "unnamed"} (${value.type}, ${value.size} bytes)`,
          );
        } else {
          console.log(`${key}: ${value}`);
        }
      }
      console.log("=========================");

      const response = await fetch(`${API_BASE_URL}/update_event`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: payload,
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.message || `Failed to update event: ${response.status}`,
        );
      }

      // Revoke object URLs to prevent memory leaks
      if (bannerPreview) URL.revokeObjectURL(bannerPreview);
      if (logoPreview) URL.revokeObjectURL(logoPreview);

      // Redirect to dashboard with success message
      navigate("/dashboard#my-events", {
        state: { message: "Event updated successfully!" },
      });
    } catch (err) {
      console.error("Update event error:", err);
      if (err.message.includes("413")) {
        setError("File uploads are too large. Please use smaller images.");
      } else {
        setError(err.message || "Something went wrong. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (
      window.confirm(
        "Are you sure you want to cancel? All unsaved changes will be lost.",
      )
    ) {
      // Revoke object URLs
      if (bannerPreview) URL.revokeObjectURL(bannerPreview);
      if (logoPreview) URL.revokeObjectURL(logoPreview);

      navigate("/dashboard#my-events");
    }
  };

  // Step navigation
  const handleNextStep = () => {
    if (currentStep === 1) {
      // Validate basic info before proceeding
      if (
        !formData.event_type_id ||
        !formData.name ||
        !formData.start_date ||
        !formData.end_date ||
        !formData.venue ||
        !formData.city ||
         !formData.organization_name?.trim() ||
            !formData.contact_email?.trim()
      ) {
        setError("Please fill in all required fields marked with *");
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  // Get selected event type name
  const getSelectedEventTypeName = () => {
    const selectedType = eventTypes.find(
      (type) => type.id === formData.event_type_id,
    );
    return selectedType ? selectedType.type : "Not selected";
  };

  // Get selected event type for display
  const getSelectedEventType = () => {
    return eventTypes.find((type) => type.id === formData.event_type_id);
  };

  // Form builder handlers
  const handleOpenCustomFormBuilder = (type) => {
    setCustomFormType(type);
    setShowFormBuilderModal(true);
  };

  const handleFormBuilderSave = (formType, customFormFields) => {
    if (formType === "attendee") {
      setAttendeeFields((prev) => ({
        ...prev,
        registration_form_type: "custom",
        custom_form_fields: customFormFields,
      }));
    } else if (formType === "speaker") {
      setSpeakerFields((prev) => ({
        ...prev,
        speaker_form_type: "custom",
        custom_speaker_form: customFormFields,
      }));
    }
  };



  // Render event type cards - Read-only version
  const renderEventTypeCards = () => {
    const selectedType = getSelectedEventType();

    if (selectedType) {
      return (
        <div className="event-type-card selected read-only">
          <div className="event-type-icon">
            <i className={selectedType.icon || EVENT_TYPE_ICONS.default} />
          </div>
          <div className="event-type-content">
            <h4 className="event-type-name">{selectedType.type}</h4>
            <p className="event-type-desc">
              {getEventTypeDescription(selectedType.type)}
            </p>
            <div className="read-only-badge">
              <i className="fas fa-lock" /> Event Type (Cannot be changed)
            </div>
          </div>
        </div>
      );
    }




    return (
      <div className="event-type-card read-only empty">
        <div className="event-type-icon">
          <i className="fas fa-calendar" />
        </div>
        <div className="event-type-content">
          <h4 className="event-type-name">No Event Type Selected</h4>
          <p className="event-type-desc">
            Event type cannot be changed after creation
          </p>
          <div className="read-only-badge">
            <i className="fas fa-lock" /> Event Type (Cannot be changed)
          </div>
        </div>
      </div>
    );
  };

  // Render default form preview
  const renderDefaultFormPreview = () => {
    const isSpeakerForm = activeTab === "speaker";
    const title = isSpeakerForm
      ? "Speaker Registration Form Preview"
      : "Attendee Registration Form Preview";

    const hasCustomForm = isSpeakerForm
      ? speakerFields.speaker_form_type === "custom" &&
      speakerFields.custom_speaker_form.length > 0
      : attendeeFields.registration_form_type === "custom" &&
      attendeeFields.custom_form_fields.length > 0;

    const customFields = isSpeakerForm
      ? speakerFields.custom_speaker_form
      : attendeeFields.custom_form_fields;

    const renderCustomFieldPreview = (field, index) => {
      const label = field.label || `Field ${index + 1}`;
      switch (field.field_type) {
        case "TextArea":
          return (
            <div className="form-group" key={index}>
              <label>
                {label}{" "}
                {field.is_required && <span className="required">*</span>}
              </label>
              <textarea
                className="form-input"
                placeholder={field.place_holder || ""}
                disabled
                rows="3"
              ></textarea>
            </div>
          );
        case "Dropdown":
          return (
            <div className="form-group" key={index}>
              <label>
                {label}{" "}
                {field.is_required && <span className="required">*</span>}
              </label>
              <select className="form-select" disabled>
                <option>{field.place_holder || "Select an option"}</option>
                {(field.options || []).map((opt, i) => (
                  <option key={i}>{opt}</option>
                ))}
              </select>
            </div>
          );
        case "RadioGroup":
          return (
            <div className="form-group" key={index}>
              <label>
                {label}{" "}
                {field.is_required && <span className="required">*</span>}
              </label>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  marginTop: "4px",
                }}
              >
                {(field.options || []).map((opt, i) => (
                  <label
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontWeight: "normal",
                      cursor: "default",
                    }}
                  >
                    <input type="radio" disabled /> {opt}
                  </label>
                ))}
              </div>
            </div>
          );
        case "Checkbox":
          return (
            <div className="form-group" key={index}>
              <label
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <input type="checkbox" disabled />
                {label}{" "}
                {field.is_required && <span className="required">*</span>}
              </label>
            </div>
          );
        default:
          const inputType =
            field.field_type === "EmailInput"
              ? "email"
              : field.field_type === "PhoneInput"
                ? "tel"
                : field.field_type === "NumberInput"
                  ? "number"
                  : field.field_type === "DatePicker"
                    ? "date"
                    : "text";
          return (
            <div className="form-group" key={index}>
              <label>
                {label}{" "}
                {field.is_required && <span className="required">*</span>}
              </label>
              <input
                type={inputType}
                className="form-input"
                placeholder={field.place_holder || ""}
                disabled
              />
            </div>
          );
      }
    };

    return (
      <div className="modal-overlay">
        <div className="modal-content form-preview-modal">
          <div className="modal-header">
            <h3>{title}</h3>
            <button
              type="button"
              className="close-modal"
              onClick={() => setShowDefaultFormPreview(false)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="modal-body">
            <div className="form-preview">
              <h4>
                {hasCustomForm
                  ? "Custom Registration Form"
                  : "Default Registration Form"}
              </h4>
              <div className="preview-form">
                {hasCustomForm ? (
                  <>
                    {customFields.map((field, index) =>
                      renderCustomFieldPreview(field, index),
                    )}
                    <button type="button" className="btn btn-primary" disabled>
                      {isSpeakerForm ? "Submit Proposal" : "Register Now"}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label>
                        Full Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Enter your full name"
                        disabled
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Email Address <span className="required">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-input"
                        placeholder="Enter your email address"
                        disabled
                      />
                    </div>
                    {isSpeakerForm ? (
                      <>
                        <div className="form-group">
                          <label>
                            Talk Topic <span className="required">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Enter your talk topic"
                            disabled
                          />
                        </div>
                        <div className="form-group">
                          <label>
                            Talk Detail <span className="required">*</span>
                          </label>
                          <textarea
                            className="form-input"
                            placeholder="Describe your talk"
                            disabled
                            rows="4"
                          ></textarea>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="form-group">
                          <label>Phone Number</label>
                          <input
                            type="tel"
                            className="form-input"
                            placeholder="Enter your phone number"
                            disabled
                          />
                        </div>
                        <div className="form-group">
                          <label>Organization</label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Enter your organization"
                            disabled
                          />
                        </div>
                      </>
                    )}
                    <button type="button" className="btn btn-primary" disabled>
                      {isSpeakerForm ? "Submit Proposal" : "Register Now"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render sponsor management section
  const renderSponsorManagement = () => (
    <div className="sponsor-management">
      <div className="section-header">
        <h4>Sponsor Management</h4>
        <p>Add sponsors with their details</p>
      </div>

      <div className="sponsor-form">
        <div className="form-group">
          <label>Sponsor Name *</label>
          <input
            type="text"
            className="form-input"
            value={newSponsorName}
            onChange={(e) => setNewSponsorName(e.target.value)}
            placeholder="Enter sponsor name"
          />
        </div>

        <div className="form-group">
          <label>Sponsor Category *</label>
          <select
            className="form-select"
            value={newSponsorCategory}
            onChange={(e) => setNewSponsorCategory(e.target.value)}
          >
            <option value="">Select Category</option>
            {sponsorCategories.map((cat) => (
              <option key={cat.id} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Sponsor Logo *</label>
          <div
            className="file-upload-card"
            onClick={() => document.getElementById("sponsorLogo").click()}
          >
            <div className="file-upload-icon">
              <i className="fas fa-camera" />
            </div>
            <div className="file-upload-content">
              <div className="file-upload-title">Upload Logo</div>
              <div className="file-upload-subtitle">Optional logo image</div>
            </div>
          </div>
          <input
            type="file"
            id="sponsorLogo"
            name="sponsorLogo"
            onChange={handleSponsorLogoChange}
            accept="image/*"
            style={{ display: "none" }}
          />
          {newSponsorLogo && (
            <div className="file-preview">
              <span>{newSponsorLogo.name}</span>
              <button
                type="button"
                className="remove-file-btn"
                onClick={() => setNewSponsorLogo(null)}
              >
                <i className="fas fa-times" />
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          className="btn btn-primary"
          onClick={addSponsor}
          disabled={!newSponsorName.trim()}
        >
          <i className="fas fa-plus" /> Add Sponsor
        </button>
      </div>

      <div className="sponsor-list">
        <h5>Added Sponsors ({sponsorFields.sponsor_names.length})</h5>
        {sponsorFields.sponsor_names.length === 0 ? (
          <p className="empty-message">No sponsors added yet.</p>
        ) : (
          <div className="sponsor-items">
            {sponsorFields.sponsor_names.map((sponsor, index) => (
              <div key={index} className="sponsor-item">
                <div className="sponsor-info">
                  <span className="sponsor-name">{sponsor}</span>
                  <span className="sponsor-files">
                    {(sponsorFields.sponsor_logos[index] ||
                      sponsorFields.sponsor_logo_urls[index]) && (
                        <i className="fas fa-camera" title="Has logo" />
                      )}
                  </span>
                </div>
                <button
                  type="button"
                  className="remove-sponsor-btn"
                  onClick={() => removeSponsor(index)}
                >
                  <i className="fas fa-times" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Render exhibitor management section
  const renderExhibitorManagement = () => (
    <div className="exhibitor-management">
      <div className="section-header">
        <h4>Exhibitor Management</h4>
        <p>Add exhibitors with their details</p>
      </div>

      <div className="exhibitor-form">
        <div className="form-group">
          <label>Exhibitor Name *</label>
          <input
            type="text"
            className="form-input"
            value={newExhibitorName}
            onChange={(e) => setNewExhibitorName(e.target.value)}
            placeholder="Enter exhibitor name"
          />
        </div>

        <div className="form-group">
          <label>Exhibitor Logo *</label>
          <div
            className="file-upload-card"
            onClick={() => document.getElementById("exhibitorLogo").click()}
          >
            <div className="file-upload-icon">
              <i className="fas fa-camera" />
            </div>
            <div className="file-upload-content">
              <div className="file-upload-title">Upload Logo</div>
              <div className="file-upload-subtitle">Optional logo image</div>
            </div>
          </div>
          <input
            type="file"
            id="exhibitorLogo"
            name="exhibitorLogo"
            onChange={handleExhibitorLogoChange}
            accept="image/*"
            style={{ display: "none" }}
          />
          {newExhibitorLogo && (
            <div className="file-preview">
              <span>{newExhibitorLogo.name}</span>
              <button
                type="button"
                className="remove-file-btn"
                onClick={() => setNewExhibitorLogo(null)}
              >
                <i className="fas fa-times" />
              </button>
            </div>
          )}
        </div>

        <div className="form-group"></div>

        <button
          type="button"
          className="btn btn-primary"
          onClick={addExhibitor}
          disabled={!newExhibitorName.trim()}
        >
          <i className="fas fa-plus" /> Add Exhibitor
        </button>
      </div>

      <div className="exhibitor-list">
        <h5>Added Exhibitors ({exhibitorFields.exhibitor_names.length})</h5>
        {exhibitorFields.exhibitor_names.length === 0 ? (
          <p className="empty-message">No exhibitors added yet.</p>
        ) : (
          <div className="exhibitor-items">
            {exhibitorFields.exhibitor_names.map((exhibitor, index) => (
              <div key={index} className="exhibitor-item">
                <div className="exhibitor-info">
                  <span className="exhibitor-name">{exhibitor}</span>
                  <span className="exhibitor-files">
                    {(exhibitorFields.exhibitor_logos[index] ||
                      exhibitorFields.exhibitor_logo_urls[index]) && (
                        <i className="fas fa-camera" title="Has logo" />
                      )}
                  </span>
                </div>
                <button
                  type="button"
                  className="remove-exhibitor-btn"
                  onClick={() => removeExhibitor(index)}
                >
                  <i className="fas fa-times" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Render Step 1 - Basic Information
  const renderStep1 = () => (
    <div className="form-section" id="basicInfoSection">
      <div className="section-header">
        <h2 className="section-title">
          <i className="fas fa-edit" /> Edit Event Information
        </h2>
        <p className="section-subtitle">
          Update the basic details of your event
        </p>
      </div>

      <div className="form-grid">
        <div className="form-group full-width">
          <label className="form-label">
            Event Type <span className="required">*</span>
            {usingFallbackData && (
              <span className="form-note">(Using default options)</span>
            )}
          </label>
          <div className="event-types-grid">{renderEventTypeCards()}</div>
          <div className="read-only-field">
            <div className="read-only-label">Selected Event Type ID:</div>
            <div className="read-only-value">
              {formData.event_type_id || "Not selected"}
            </div>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="name">
            Event Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            className="form-input"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Enter event name"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="venue">
            Venue <span className="required">*</span>
          </label>
          <input
            type="text"
            id="venue"
            name="venue"
            className="form-input"
            value={formData.venue}
            onChange={handleChange}
            required
            placeholder="Event location"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="city">
            City <span className="required">*</span>
          </label>
          <select
            id="city"
            name="city"
            className="form-select"
            value={formData.city}
            onChange={handleChange}
            required
          >
            <option value="">Select city</option>
            {CITIES.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="start_date">
            Start Date & Time <span className="required">*</span>
          </label>
          <div className="datetime-input-container">
            <input
              type="date"
              id="start_date"
              name="start_date"
              className="form-input date-input"
              value={formData.start_date.split("T")[0] || ""}
              onChange={(e) => {
                const date = e.target.value;
                const time = formData.start_date.split("T")[1] || "00:00";
                setFormData({ ...formData, start_date: `${date}T${time}` });
              }}
              required
            />
            <input
              type="time"
              className="form-input time-input"
              value={formData.start_date.split("T")[1] || "00:00"}
              onChange={(e) => {
                const time = e.target.value;
                const date = formData.start_date.split("T")[0] || "";
                setFormData({ ...formData, start_date: `${date}T${time}` });
              }}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="end_date">
            End Date & Time <span className="required">*</span>
          </label>
          <div className="datetime-input-container">
            <input
              type="date"
              id="end_date"
              name="end_date"
              className="form-input date-input"
              value={formData.end_date.split("T")[0] || ""}
              onChange={(e) => {
                const date = e.target.value;
                const time = formData.end_date.split("T")[1] || "00:00";
                setFormData({ ...formData, end_date: `${date}T${time}` });
              }}
              required
            />
            <input
              type="time"
              className="form-input time-input"
              value={formData.end_date.split("T")[1] || "00:00"}
              onChange={(e) => {
                const time = e.target.value;
                const date = formData.end_date.split("T")[0] || "";
                setFormData({ ...formData, end_date: `${date}T${time}` });
              }}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">
            Pricing Type <span className="required">*</span>
          </label>
          <div className="pricing-options">
            <button
              type="button"
              className={`pricing-option-btn ${!formData.is_paid ? "active" : ""}`}
              onClick={() =>
                setFormData({ ...formData, is_paid: false, ticket_price: "" })
              }
            >
              <i className="fas fa-ticket-alt" /> Free Event
            </button>
            <button
              type="button"
              className={`pricing-option-btn ${formData.is_paid ? "active" : ""}`}
              onClick={() => setFormData({ ...formData, is_paid: true })}
            >
              <i className="fas fa-dollar-sign" /> Paid Event
            </button>
          </div>
        </div>

        {formData.is_paid && (
          <div className="form-group">
            <label className="form-label" htmlFor="ticket_price">
              Ticket Price ($) <span className="required">*</span>
            </label>
            <div className="price-input-container">
              <span className="price-prefix">$</span>
              <input
                type="number"
                id="ticket_price"
                name="ticket_price"
                className="form-input price-input"
                min="0"
                step="0.01"
                value={formData.ticket_price}
                onChange={handleChange}
                placeholder="0.00"
                required={formData.is_paid}
              />
            </div>
          </div>
        )}

        <div className="form-group full-width">
          <label className="form-label" htmlFor="description">
            Event Description
          </label>
          <textarea
            id="description"
            name="description"
            className="form-textarea"
            value={formData.description}
            onChange={handleChange}
            rows="4"
            placeholder="Describe your event..."
          />
          <div className="char-counter">
            {formData.description.length}/1000 characters
          </div>
        </div>


        {/* Contact Information Section */}
        <div className="form-group full-width">
          <h3 style={{ marginBottom: "15px", fontSize: "18px" }}>
            <i className="fas fa-address-card" /> Contact Information
          </h3>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px" }}>

            <div className="form-group">
              <label className="form-label" htmlFor="organization_name">
                Organizer Name<span style={{ color: "red" }}>*</span>
              </label>

              <input
                type="text"
                id="organization_name"
                name="organization_name"
                className="form-input"
                value={formData.organization_name}
                onChange={handleChange}
                placeholder="Enter organization name"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="contact_email">
                Contact Email <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="email"
                id="contact_email"
                name="contact_email"
                className="form-input"
                value={formData.contact_email || ""}
                onChange={handleChange}
                placeholder="contact@event.com"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="instagram_url">
                <i className="fab fa-instagram" /> Instagram
              </label>
              <input
                type="url"
                id="instagram_url"
                name="instagram_url"
                className="form-input"
                value={formData.instagram_url || ""}
                onChange={handleChange}
                placeholder="https://instagram.com/your-event"
              />
            </div>



            <div className="form-group">
              <label className="form-label" htmlFor="linkedin_url">
                <i className="fab fa-linkedin" /> LinkedIn
              </label>
              <input
                type="url"
                id="linkedin_url"
                name="linkedin_url"
                className="form-input"
                value={formData.linkedin_url || ""}
                onChange={handleChange}
                placeholder="https://linkedin.com/company/your-event"
              />
            </div>






          </div>



          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px" }}>




<div className="form-group">
  <label className="form-label" htmlFor="contact_phone">
    Contact Phone
  </label>

  <input
    type="tel"
    id="contact_phone"
    name="contact_phone"
    className="form-input"
    value={formData.contact_phone || ""}
    onChange={(e) => {
      const value = e.target.value.replace(/\D/g, "").slice(0, 10);

      handleChange({
        target: {
          name: "contact_phone",
          value,
        },
      });
    }}
    placeholder="Enter 10 digit phone number"
    maxLength={10}
    pattern="[0-9]{10}"
    inputMode="numeric"
    required
  />
</div>

            <div className="form-group">
              <label className="form-label" htmlFor="twitter_url">
                <i className="fab fa-twitter" /> Twitter/X
              </label>
              <input
                type="url"
                id="twitter_url"
                name="twitter_url"
                className="form-input"
                value={formData.twitter_url || ""}
                onChange={handleChange}
                placeholder="https://twitter.com/your-event"
              />
            </div>



            <div className="form-group">
              <label className="form-label" htmlFor="website_url">
                <i className="fas fa-globe" /> Website
              </label>
              <input
                type="url"
                id="website_url"
                name="website_url"
                className="form-input"
                value={formData.website_url || ""}
                onChange={handleChange}
                placeholder="https://www.event-website.com"
              />
            </div>
          </div>
        </div>








        <div className="form-group">
          <label className="form-label">Event Banner</label>
          <div
            className="file-upload-card"
            onClick={() => document.getElementById("banner").click()}
          >
            <div className="file-upload-icon">
              <i className="fas fa-image" />
            </div>
            <div className="file-upload-content">
              <div className="file-upload-title">
                {bannerPreview ? "Change Banner" : "Upload Banner"}
              </div>
              <div className="file-upload-subtitle">
                PNG, JPG, GIF up to 5MB
              </div>
              {existingBannerUrl && !banner && (
                <div
                  className="file-upload-subtitle"
                  style={{ marginTop: "5px", color: "#4f46e5" }}
                >
                  <i className="fas fa-check-circle" /> Current banner exists
                </div>
              )}
            </div>
          </div>
          <input
            type="file"
            id="banner"
            name="banner"
            onChange={handleBannerChange}
            accept="image/*"
            style={{ display: "none" }}
          />
          {(bannerPreview || existingBannerUrl) && (
            <div className="image-preview">
              <img
                src={bannerPreview || existingBannerUrl}
                alt="Banner preview"
              />
              <button
                type="button"
                className="remove-image-btn"
                onClick={() => {
                  setBanner(null);
                  setBannerPreview(null);
                  setExistingBannerUrl(null);
                  setBannerFileName("");
                }}
              >
                <i className="fas fa-times" />
              </button>
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Event Logo</label>
          <div
            className="file-upload-card"
            onClick={() => document.getElementById("logo").click()}
          >
            <div className="file-upload-icon">
              <i className="fas fa-camera" />
            </div>
            <div className="file-upload-content">
              <div className="file-upload-title">
                {logoPreview ? "Change Logo" : "Upload Logo"}
              </div>
              <div className="file-upload-subtitle">PNG, JPG up to 5MB</div>
              {existingLogoUrl && !logo && (
                <div
                  className="file-upload-subtitle"
                  style={{ marginTop: "5px", color: "#4f46e5" }}
                >
                  <i className="fas fa-check-circle" /> Current logo exists
                </div>
              )}
            </div>
          </div>
          <input
            type="file"
            id="logo"
            name="logo"
            onChange={handleLogoChange}
            accept="image/*"
            style={{ display: "none" }}
          />
          {(logoPreview || existingLogoUrl) && (
            <div className="image-preview">
              <img src={logoPreview || existingLogoUrl} alt="Logo preview" />
              <button
                type="button"
                className="remove-image-btn"
                onClick={() => {
                  setLogo(null);
                  setLogoPreview(null);
                  // Keep existing URL if available
                  if (!existingLogoUrl) {
                    setLogoPreview(null);
                  }
                }}
              >
                <i className="fas fa-times" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn btn-outline"
          onClick={handleCancel}
        >
          <i className="fas fa-times" /> Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleNextStep}
          disabled={
            !formData.event_type_id ||
            !formData.name ||
            !formData.start_date ||
            !formData.end_date ||
            !formData.venue ||
            !formData.city ||
             !formData.organization_name?.trim() ||
            !formData.contact_email?.trim()
          }
        >
          Next: Advanced Configuration <i className="fas fa-arrow-right" />
        </button>
      </div>
    </div>
  );

  // Render Step 2 - Advanced Configuration
  const renderStep2 = () => (
    <div className="form-section" id="advancedConfigSection">
      <div className="section-header">
        <h2 className="section-title">
          <i className="fas fa-cogs" /> Update Configuration
        </h2>
        <p className="section-subtitle">
          Update registration and participation options
        </p>
      </div>

      <div className="config-tabs-wrapper">
        <div className="config-tabs">
          <button
            type="button"
            className={`config-tab ${activeTab === "attendee" ? "active" : ""}`}
            onClick={() => setActiveTab("attendee")}
          >
            <i className="fas fa-users" /> Attendee
          </button>
          <button
            type="button"
            className={`config-tab ${activeTab === "speaker" ? "active" : ""}`}
            onClick={() => setActiveTab("speaker")}
          >
            <i className="fas fa-microphone" /> Speaker
          </button>
          <button
            type="button"
            className={`config-tab ${activeTab === "sponsor" ? "active" : ""}`}
            onClick={() => setActiveTab("sponsor")}
          >
            <i className="fas fa-handshake" /> Sponsor
          </button>
          <button
            type="button"
            className={`config-tab ${activeTab === "exhibitor" ? "active" : ""}`}
            onClick={() => setActiveTab("exhibitor")}
          >
            <i className="fas fa-store" /> Exhibitor
          </button>
        </div>

        <div className="config-content">
          {/* Attendee Configuration */}
          {activeTab === "attendee" && (
            <div className="config-panel">
              <div className="toggle-header">
                <div className="toggle-label">
                  <i className="fas fa-users" /> Enable Attendee Registration
                </div>

                <label className="event-toggle">
                  <input
                    type="checkbox"
                    checked={enableAttendee}
                    onChange={(e) => setEnableAttendee(e.target.checked)}
                  />
                  <span className="event-toggle-track">
                    <span className="event-toggle-text">
                      {enableAttendee ? "ON" : "OFF"}
                    </span>
                    <span className="event-toggle-thumb"></span>
                  </span>
                </label>


              </div>

              {enableAttendee && (
                <div className="config-fields">
                  <div className="form-grid compact">
                    <div className="form-group">
                      <label className="form-label">
                        Registration Start Date
                      </label>
                      <input
                        type="date"
                        name="registration_start_date"
                        className="form-input"
                        value={attendeeFields.registration_start_date}
                        onChange={handleAttendeeChange}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        Registration End Date
                      </label>
                      <input
                        type="date"
                        name="registration_end_date"
                        className="form-input"
                        value={attendeeFields.registration_end_date}
                        onChange={handleAttendeeChange}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Attendee Limit</label>
                      <input
                        type="number"
                        name="registration_limit"
                        className="form-input"
                        min="1"
                        value={attendeeFields.registration_limit}
                        onChange={handleAttendeeChange}
                        placeholder="Unlimited"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        Registration Approval
                      </label>
                      <select
                        className="form-select"
                        value={
                          attendeeFields.is_auto_approve ? "auto" : "manual"
                        }
                        onChange={(e) =>
                          setAttendeeFields((prev) => ({
                            ...prev,
                            is_auto_approve: e.target.value === "auto",
                          }))
                        }
                      >
                        <option value="auto">Auto-approve</option>
                        <option value="manual">Manual approval</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        Registration Form Type
                      </label>
                      <div className="form-type-options">
                        <button
                          type="button"
                          className={`form-type-btn ${attendeeFields.registration_form_type === "default" ? "active" : ""}`}
                          onClick={() => {
                            setActiveTab("attendee");
                            setShowDefaultFormPreview(true);
                          }}
                        >
                          <i className="fas fa-eye" /> Preview Form
                        </button>
<div className="custom-form-btn-wrapper">
  <button
    type="button"
    className={`form-type-btn ${
      attendeeFields.registration_form_type === "custom" ? "active" : ""
    } disabled-custom-form-btn`}
    disabled={true}
  >
    <i className="fas fa-pen" /> Custom Form
  </button>

  <div className="custom-form-tooltip">
    Custom form can only be changed or edited during event creation.
  </div>
</div>

                      </div>
                      {attendeeFields.registration_form_type === "custom" && (
                        <div className="custom-form-indicator">
                          <i className="fas fa-check-circle"></i>
                          {attendeeFields.custom_form_fields.length} custom
                          field(s) configured
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Speaker Configuration */}
          {activeTab === "speaker" && (
            <div className="config-panel">
              <div className="toggle-header">
                <div className="toggle-label">
                  <i className="fas fa-microphone" /> Enable Speaker
                  Registration
                </div>
                <label className="event-toggle">
                  <input
                    type="checkbox"
                    checked={enableSpeaker}
                    onChange={(e) => setEnableSpeaker(e.target.checked)}
                  />
                  <span className="event-toggle-track">
                    <span className="event-toggle-text">
                      {enableSpeaker ? "ON" : "OFF"}
                    </span>
                    <span className="event-toggle-thumb"></span>
                  </span>
                </label>
              </div>

              {enableSpeaker && (
                <div className="config-fields">
                  <div className="form-grid compact">
                    <div className="form-group">
                      <label className="form-label">CFP Start Date</label>
                      <input
                        type="date"
                        name="speaker_start_date"
                        className="form-input"
                        value={speakerFields.speaker_start_date}
                        onChange={handleSpeakerChange}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">CFP End Date</label>
                      <input
                        type="date"
                        name="speaker_end_date"
                        className="form-input"
                        value={speakerFields.speaker_end_date}
                        onChange={handleSpeakerChange}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Speaker Limit</label>
                      <input
                        type="number"
                        name="speaker_limit"
                        className="form-input"
                        min="1"
                        value={speakerFields.speaker_limit}
                        onChange={handleSpeakerChange}
                        placeholder="Maximum speakers"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Speaker Form Type</label>
                      <div className="form-type-options">
                        <button
                          type="button"
                          className={`form-type-btn ${speakerFields.speaker_form_type === "default" ? "active" : ""}`}
                          onClick={() => {
                            setActiveTab("speaker");
                            setShowDefaultFormPreview(true);
                          }}
                        >
                          <i className="fas fa-eye" /> Preview Form
                        </button>
                        <div className="custom-form-btn-wrapper">
  <button
    type="button"
    className={`form-type-btn ${
      attendeeFields.registration_form_type === "custom" ? "active" : ""
    } disabled-custom-form-btn`}
    disabled={true}
  >
    <i className="fas fa-pen" /> Custom Form
  </button>

  <div className="custom-form-tooltip">
    Custom form can only be changed or edited during event creation.
  </div>
</div>
                      </div>
                      {speakerFields.speaker_form_type === "custom" && (
                        <div className="custom-form-indicator">
                          <i className="fas fa-check-circle"></i>
                          {speakerFields.custom_speaker_form.length} custom
                          field(s) configured
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sponsor Configuration */}
          {activeTab === "sponsor" && (
            <div className="config-panel">
              <div className="toggle-header">
                <div className="toggle-label">
                  <i className="fas fa-handshake" /> Enable Sponsor Registration
                </div>
                <label className="event-toggle">
                  <input
                    type="checkbox"
                    checked={enableSponsor}
                    onChange={(e) => setEnableSponsor(e.target.checked)}
                  />
                  <span className="event-toggle-track">
                    <span className="event-toggle-text">
                      {enableSponsor ? "ON" : "OFF"}
                    </span>
                    <span className="event-toggle-thumb"></span>
                  </span>
                </label>
              </div>

              {enableSponsor && (
                <div className="config-fields">{renderSponsorManagement()}</div>
              )}
            </div>
          )}

          {/* Exhibitor Configuration */}
          {activeTab === "exhibitor" && (
            <div className="config-panel">
              <div className="toggle-header">
                <div className="toggle-label">
                  <i className="fas fa-store" /> Enable Exhibitor Registration
                </div>
                <label className="event-toggle">
                  <input
                    type="checkbox"
                    checked={enableExhibitor}
                    onChange={(e) => setEnableExhibitor(e.target.checked)}
                  />
                  <span className="event-toggle-track">
                    <span className="event-toggle-text">
                      {enableExhibitor ? "ON" : "OFF"}
                    </span>
                    <span className="event-toggle-thumb"></span>
                  </span>
                </label>
              </div>

              {enableExhibitor && (
                <div className="config-fields">
                  {renderExhibitorManagement()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn btn-outline"
          onClick={handlePrevStep}
        >
          <i className="fas fa-arrow-left" /> Back to Basic Info
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleNextStep}
        >
          Next: Preview & Submit <i className="fas fa-arrow-right" />
        </button>
      </div>
    </div>
  );

  // Render Step 3 - Preview & Submit
  const renderStep3 = () => (
    <div className="form-section" id="previewSection">
      <div className="section-header">
        <h2 className="section-title">
          <i className="fas fa-eye" /> Preview & Update
        </h2>
        <p className="section-subtitle">
          Review your changes before updating the event
        </p>
      </div>

      <div className="preview-container">
        <div className="preview-summary">
          <div className="preview-header">
            <div className="preview-event-type">
              <i
                className={
                  eventTypes.find((t) => t.id === formData.event_type_id)
                    ?.icon || "fas fa-calendar"
                }
              />
              <span>{getSelectedEventTypeName()}</span>
            </div>
            <h3 className="preview-event-name">
              {formData.name || "Untitled Event"}
            </h3>
          </div>

          <div className="preview-grid">
            <div className="preview-card">
              <h4 className="preview-card-title">
                <i className="fas fa-calendar-alt" /> Event Details
              </h4>
              <div className="preview-detail">
                <span className="preview-label">Date & Time:</span>
                <span className="preview-value">
                  {formData.start_date
                    ? new Date(formData.start_date).toLocaleString()
                    : "Not set"}
                </span>
              </div>
              <div className="preview-detail">
                <span className="preview-label">Duration:</span>
                <span className="preview-value">
                  {formData.end_date
                    ? `${Math.round((new Date(formData.end_date) - new Date(formData.start_date)) / (1000 * 60 * 60))} hours`
                    : "Not set"}
                </span>
              </div>
              <div className="preview-detail">
                <span className="preview-label">Location:</span>
                <span className="preview-value">
                  {formData.venue}, {formData.city}
                </span>
              </div>
              <div className="preview-detail">
                <span className="preview-label">Pricing:</span>
                <span className="preview-value pricing-tag">
                  {formData.is_paid
                    ? `$${formData.ticket_price || "0.00"}`
                    : "Free"}
                </span>
              </div>
            </div>

            <div className="preview-card">
              <h4 className="preview-card-title">
                <i className="fas fa-users" /> Registration Status
              </h4>
              <div className="preview-detail">
                <span className="preview-label">Attendees:</span>
                <span
                  className={`preview-value status-badge ${enableAttendee ? "enabled" : "disabled"}`}
                >
                  {enableAttendee ? "Enabled" : "Disabled"}
                </span>
              </div>
              {enableAttendee && (
                <>
                  <div className="preview-detail">
                    <span className="preview-label">Limit:</span>
                    <span className="preview-value">
                      {attendeeFields.registration_limit || "Unlimited"}
                    </span>
                  </div>
                  <div className="preview-detail">
                    <span className="preview-label">Approval:</span>
                    <span className="preview-value">
                      {attendeeFields.is_auto_approve
                        ? "Auto-approve"
                        : "Manual approval"}
                    </span>
                  </div>
                  <div className="preview-detail">
                    <span className="preview-label">Form Type:</span>
                    <span className="preview-value">
                      {attendeeFields.registration_form_type}
                      {attendeeFields.registration_form_type === "custom" &&
                        ` (${attendeeFields.custom_form_fields.length} fields)`}
                    </span>
                  </div>
                </>
              )}
              <div className="preview-detail">
                <span className="preview-label">Speakers:</span>
                <span
                  className={`preview-value status-badge ${enableSpeaker ? "enabled" : "disabled"}`}
                >
                  {enableSpeaker ? "Enabled" : "Disabled"}
                </span>
              </div>
              {enableSpeaker && (
                <div className="preview-detail">
                  <span className="preview-label">Form Type:</span>
                  <span className="preview-value">
                    {speakerFields.speaker_form_type}
                    {speakerFields.speaker_form_type === "custom" &&
                      ` (${speakerFields.custom_speaker_form.length} fields)`}
                  </span>
                </div>
              )}
              <div className="preview-detail">
                <span className="preview-label">Sponsors:</span>
                <span
                  className={`preview-value status-badge ${enableSponsor ? "enabled" : "disabled"}`}
                >
                  {enableSponsor
                    ? `Enabled (${sponsorFields.sponsor_names.length})`
                    : "Disabled"}
                </span>
              </div>
              <div className="preview-detail">
                <span className="preview-label">Exhibitors:</span>
                <span
                  className={`preview-value status-badge ${enableExhibitor ? "enabled" : "disabled"}`}
                >
                  {enableExhibitor
                    ? `Enabled (${exhibitorFields.exhibitor_names.length})`
                    : "Disabled"}
                </span>
              </div>
            </div>

            <div className="preview-card">
              <h4 className="preview-card-title">
                <i className="fas fa-images" /> Media
              </h4>
              <div className="preview-detail">
                <span className="preview-label">Banner:</span>
                <span className="preview-value">
                  {banner
                    ? "New upload"
                    : existingBannerUrl
                      ? "Existing"
                      : "Not uploaded"}
                </span>
              </div>
              <div className="preview-detail">
                <span className="preview-label">Logo:</span>
                <span className="preview-value">
                  {logo
                    ? "New upload"
                    : existingLogoUrl
                      ? "Existing"
                      : "Not uploaded"}
                </span>
              </div>
              {(bannerPreview || existingBannerUrl) && (
                <div className="preview-image">
                  <img
                    src={bannerPreview || existingBannerUrl}
                    alt="Banner preview"
                  />
                </div>
              )}
            </div>

            <div className="preview-card full-width">
              <h4 className="preview-card-title">
                <i className="fas fa-align-left" /> Description
              </h4>
              <div className="preview-description">
                {formData.description || "No description provided."}
              </div>
            </div>
          </div>
        </div>

        <div className="terms-box">
          <div className="terms-header">
            <i className="fas fa-file-contract" /> Update Confirmation
          </div>
          <p className="terms-text">
            By updating this event, you confirm that all changes are accurate
            and comply with EventFork's Terms of Service. Existing registrations
            will not be affected unless you modify registration settings.
          </p>
          <label className="terms-checkbox">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
            />
            <span>I confirm these updates</span>
          </label>
        </div>
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn btn-outline"
          onClick={handlePrevStep}
        >
          <i className="fas fa-arrow-left" /> Back to Configuration
        </button>
        <button
          type="submit"
          className="btn btn-primary btn-submit"
          disabled={saving || !termsAccepted}
        >
          {saving ? (
            <>
              <i className="fas fa-spinner fa-spin" /> Updating Event...
            </>
          ) : (
            <>
              <i className="fas fa-save" /> Update Event
            </>
          )}
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="create-event-page">
        <div className="container">
          <div className="loading-container" style={{ marginTop: "100px" }}>
            <div className="loading-spinner"></div>
            <p>Loading event data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-event-page">
      <div className="container create-event-container">
        <div className="page-header">
          <h1 className="page-title">
            <i className="fas fa-edit" /> Edit Event
          </h1>
          <p className="page-subtitle">
            Update your event details and configuration
          </p>
        </div>

        <div className="progress-steps1">
          <div className={`progress-step ${currentStep >= 1 ? "active" : ""}`}>
            <div className="step-number">1</div>
            <div className="step-info">
              <div className="step-title">Basic Info</div>
              <div className="step-description">Event details</div>
            </div>
          </div>
          <div className={`progress-step ${currentStep >= 2 ? "active" : ""}`}>
            <div className="step-number">2</div>
            <div className="step-info">
              <div className="step-title">Configuration</div>
              <div className="step-description">Registration setup</div>
            </div>
          </div>
          <div className={`progress-step ${currentStep >= 3 ? "active" : ""}`}>
            <div className="step-number">3</div>
            <div className="step-info">
              <div className="step-title">Review</div>
              <div className="step-description">Final preview</div>
            </div>
          </div>
        </div>

        <div className="form-card">
          {error && (
            <div className="error-banner">
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </form>
        </div>
      </div>

      {/* Default Form Preview Modal */}
      {showDefaultFormPreview && renderDefaultFormPreview()}

      {/* Custom Form Builder Modal */}
      {showFormBuilderModal && (
        <FormBuilderModal
          formType={customFormType}
          onSave={handleFormBuilderSave}
          onClose={() => setShowFormBuilderModal(false)}
        />
      )}
    </div>
  );
};

export default EditEvent;
